import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, Select, TOAST_TYPE } from '@base'
import { MB_ROLES, strings } from '@constants'
import { getAllEmployees } from '@data'
import { updateApprovedCreditLimit } from '@data/conn/put/cran'
import { User } from '@data/model'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'

const deriveFormState = (data = null) => {
	const _state = {
		credit_limit: {
			id: 'credit-limit',
			label: strings('credit_limit'),
			placeholder: strings('enter', 'credit_limit'),
			value: '',
			disabled: false,
			required: false,
		},
		credit_term: {
			id: 'credit-term',
			label: strings('credit_term'),
			placeholder: strings('enter', 'credit_term'),
			value: '',
			disabled: false,
			required: false,
		},
		commodity: {
			id: 'commodity',
			label: strings('commodity'),
			placeholder: strings('enter', 'commodity'),
			value: '',
			disabled: false,
			required: false,
		},
		created_by: {
			id: 'created-by',
			label: strings('created', 'by'),
			placeholder: strings(['select', 'created', 'by']),
			value: '',
			disabled: false,
			required: false,
		},
	}

	if (data) {
		_state.commodity.value = data?.commodity
		_state.created_by.value = data?.created_by
		_state.credit_limit.value = data?.credit_limit
		_state.credit_term.value = data?.credit_term
	}
	return _state
}

const CreditLimitFinance = ({ update, cranData }) => {
	const { orgId } = useParams()
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [formState, setFormState] = useState(deriveFormState())
	const [adminList, setAdminList] = useState([])

	useEffect(() => {
		getAllEmployees({ roleFilter: MB_ROLES.FINANCE })
			.then((response) => {
				setAdminList(response.map((item) => new User(item).getUserDetails()))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				if (cranData?.financial_analyst && !cranData?.approved_credit_details) {
					updateState('created_by')(cranData?.financial_analyst)
				}
			})

		setFormState(deriveFormState(cranData?.approved_credit_details))
	}, [orgId, cranData])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const isDisabled = useMemo(() => {
		return (
			formState?.commodity?.value === '' ||
			formState?.created_by?.value === '' ||
			formState?.credit_limit?.value === '' ||
			formState?.credit_term?.value === ''
		)
	}, [formState])

	const formStateToObject = () => {
		return {
			credit_limit: formState?.credit_limit?.value,
			credit_term: formState?.credit_term?.value,
			commodity: formState?.commodity?.value,
			created_by: formState?.created_by?.value,
		}
	}

	const submitHandler = () => {
		dispatch(showLoader(true))
		const _limit = formStateToObject()

		updateApprovedCreditLimit(orgId, loggedInUser?.user_id, _limit)
			.then((response) => {
				dispatch(showToast(true, strings('msg_approved_credit_limit_updated'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('msg_approved_credit_limit_not_updated'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<Select
					id={formState?.created_by?.id}
					label={formState?.created_by?.label}
					placeholder={formState?.created_by?.placeholder}
					data={adminList ?? []}
					primaryKey='user_id'
					displayKey='full_name'
					value={adminList?.find((item) => formState?.created_by?.value?.user_id === item.user_id) ?? {}}
					onChange={updateState('created_by')}
				/>
				<Input
					id={formState?.credit_term?.id}
					label={formState?.credit_term?.label}
					placeholder={formState?.credit_term?.placeholder}
					value={formState?.credit_term?.value}
					disabled={formState?.credit_term?.disabled}
					required={formState?.credit_term?.required}
					onChange={updateState('credit_term')}
				/>
				<Input
					id={formState?.credit_limit?.id}
					label={formState?.credit_limit?.label}
					type={'number'}
					placeholder={formState?.credit_limit?.placeholder}
					value={formState?.credit_limit?.value}
					disabled={formState?.credit_limit?.disabled}
					required={formState?.credit_limit?.required}
					onChange={updateState('credit_limit')}
				/>
				<Input
					id={formState?.commodity?.id}
					label={formState?.commodity?.label}
					placeholder={formState?.commodity?.placeholder}
					value={formState?.commodity?.value}
					disabled={formState?.commodity?.disabled}
					required={formState?.commodity?.required}
					onChange={updateState('commodity')}
				/>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small disabled={isDisabled} onClick={submitHandler}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default CreditLimitFinance
